import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuditoriaList } from '../../hooks/useAuditoria'
import { api } from '../../lib/api'
import styles from '../_shared/CrudPage.module.css'

const ACAO_COR: Record<string, { bg: string; color: string }> = {
  create: { bg: '#E8F5E9', color: '#2E7D32' },
  update: { bg: '#E3F2FD', color: '#1565C0' },
  delete: { bg: '#FFEBEE', color: '#C62828' },
}

const ACAO_LABEL: Record<string, string> = {
  create: 'Criou',
  update: 'Atualizou',
  delete: 'Removeu',
}

const MODELOS_COMUNS = [
  { value: '', label: 'Todos os modelos' },
  { value: 'PMOC', label: 'PMOC' },
  { value: 'OrdemServico', label: 'Ordem de Serviço' },
  { value: 'Equipamento', label: 'Equipamento' },
  { value: 'Empresa', label: 'Empresa (cliente/planejador/executor/laboratório)' },
  { value: 'Usuario', label: 'Usuário' },
  // Mantém valores legados pra filtrar logs históricos pré-refator
  { value: 'Site', label: 'Site (legado)' },
  { value: 'Cliente', label: 'Cliente (legado)' },
  { value: 'EmpresaExecutora', label: 'Executora (legado)' },
  { value: 'EmpresaElaboradora', label: 'Elaboradora (legado)' },
]

function formatTimestamp(iso: string): string {
  try {
    const d = new Date(iso)
    return d.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return iso
  }
}

export default function AuditoriaListPage() {
  const [modelo, setModelo] = useState('')
  const [acao, setAcao] = useState('')
  const [dataGte, setDataGte] = useState('')
  const [dataLte, setDataLte] = useState('')
  const params = {
    ...(modelo ? { modelo } : {}),
    ...(acao ? { acao } : {}),
    ...(dataGte ? { data_gte: dataGte } : {}),
    ...(dataLte ? { data_lte: dataLte } : {}),
  }
  const { data, isLoading } = useAuditoriaList(params)

  async function handleExportCsv() {
    const r = await api.get('/auditoria/export/csv/', {
      params,
      responseType: 'blob',
    })
    const url = URL.createObjectURL(r.data as Blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `auditoria_${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Auditoria</h1>
          <p className={styles.subtitle}>
            Trilha de eventos do tenant — quem alterou o quê e quando.
          </p>
        </div>
      </header>

      <div className={styles.toolbar} style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
        <select
          className={styles.search}
          style={{ maxWidth: 220 }}
          value={modelo}
          onChange={(e) => setModelo(e.target.value)}
          data-testid="auditoria-filtro-modelo"
        >
          {MODELOS_COMUNS.map((m) => (
            <option key={m.value} value={m.value}>
              {m.label}
            </option>
          ))}
        </select>
        <select
          className={styles.search}
          style={{ maxWidth: 180 }}
          value={acao}
          onChange={(e) => setAcao(e.target.value)}
          data-testid="auditoria-filtro-acao"
        >
          <option value="">Todas as ações</option>
          <option value="create">Criação</option>
          <option value="update">Atualização</option>
          <option value="delete">Remoção</option>
        </select>
        <input
          type="date"
          value={dataGte}
          onChange={(e) => setDataGte(e.target.value)}
          data-testid="auditoria-filtro-data-gte"
          title="De"
          style={{ padding: '0.4rem' }}
        />
        <input
          type="date"
          value={dataLte}
          onChange={(e) => setDataLte(e.target.value)}
          data-testid="auditoria-filtro-data-lte"
          title="Até"
          style={{ padding: '0.4rem' }}
        />
        <button
          type="button"
          onClick={handleExportCsv}
          data-testid="auditoria-export-csv"
          style={{
            padding: '0.4rem 0.8rem',
            background: 'white',
            color: '#1565C0',
            border: '1px solid #1565C0',
            borderRadius: 4,
            cursor: 'pointer',
            fontSize: '0.85rem',
            fontWeight: 600,
          }}
        >
          📥 Exportar CSV
        </button>
      </div>

      {isLoading ? (
        <div className={styles.loading}>Carregando registros...</div>
      ) : (
        <section
          style={{
            background: 'white',
            borderRadius: 8,
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
            overflow: 'hidden',
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ background: '#F5F5F5' }}>
                <th style={{ padding: '0.75rem', textAlign: 'left' }}>Quando</th>
                <th style={{ padding: '0.75rem', textAlign: 'left' }}>Ação</th>
                <th style={{ padding: '0.75rem', textAlign: 'left' }}>Modelo</th>
                <th style={{ padding: '0.75rem', textAlign: 'left' }}>Objeto</th>
                <th style={{ padding: '0.75rem', textAlign: 'left' }}>Quem</th>
                <th style={{ padding: '0.75rem', textAlign: 'left' }}>IP</th>
                <th style={{ padding: '0.75rem' }}></th>
              </tr>
            </thead>
            <tbody>
              {data?.results.map((r) => {
                const meta = ACAO_COR[r.acao] ?? { bg: '#F5F5F5', color: '#616161' }
                return (
                  <tr key={r.id} style={{ borderTop: '1px solid #EEE' }}>
                    <td style={{ padding: '0.5rem', whiteSpace: 'nowrap' }}>
                      {formatTimestamp(r.criado_em)}
                    </td>
                    <td style={{ padding: '0.5rem' }}>
                      <span
                        style={{
                          background: meta.bg,
                          color: meta.color,
                          padding: '0.15rem 0.5rem',
                          borderRadius: 999,
                          fontSize: '0.75rem',
                          fontWeight: 600,
                        }}
                      >
                        {ACAO_LABEL[r.acao] ?? r.acao}
                      </span>
                    </td>
                    <td style={{ padding: '0.5rem' }}>{r.modelo}</td>
                    <td
                      style={{
                        padding: '0.5rem',
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        color: '#616161',
                      }}
                    >
                      {r.objeto_id ? r.objeto_id.slice(0, 8) + '…' : '—'}
                    </td>
                    <td style={{ padding: '0.5rem' }}>{r.usuario_email ?? '—'}</td>
                    <td
                      style={{
                        padding: '0.5rem',
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        color: '#616161',
                      }}
                    >
                      {r.ip_address ?? '—'}
                    </td>
                    <td style={{ padding: '0.5rem' }}>
                      <Link
                        to={`/auditoria/${r.id}`}
                        style={{ color: '#1565C0', fontSize: '0.85rem' }}
                      >
                        Detalhes →
                      </Link>
                    </td>
                  </tr>
                )
              })}
              {(data?.results.length ?? 0) === 0 && (
                <tr>
                  <td colSpan={7} style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                    Nenhum registro encontrado para esses filtros.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </section>
      )}

      {data && (
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 12 }}>
          {data.count} registros encontrados
        </p>
      )}
    </div>
  )
}
